'use client';

import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { GoogleIcon } from '@/components/ui/icons/google-icon';
import { LinkedInIcon } from '@/components/ui/icons/linkedin-icon';
import { MicrosoftIcon } from '@/components/ui/icons/microsoft-icon';
import { useSignInOAuth } from '@/hooks/mutations/auth/use-sign-in-oauth';
import { mapAuthError } from '@/lib/auth/auth-errors';
import type { OAuthProvider } from '@/lib/schemas/auth/oauth-provider.schema';

type Mode = 'login' | 'signup';

const PROVIDERS: Array<{
  provider: OAuthProvider;
  label: string;
  Icon: React.FC<React.SVGProps<SVGSVGElement>>;
}> = [
  { provider: 'google', label: 'Google', Icon: GoogleIcon },
  { provider: 'linkedin_oidc', label: 'LinkedIn', Icon: LinkedInIcon },
  { provider: 'azure', label: 'Microsoft', Icon: MicrosoftIcon },
];

type Props = {
  mode: Mode;
  redirectTo?: string;
};

export function AuthSsoButtons({ mode, redirectTo }: Props) {
  const t = useTranslations('auth');
  const mutation = useSignInOAuth();
  const prefix = mode === 'login' ? t('sso.loginPrefix') : t('sso.signupPrefix');
  return (
    <div className="flex flex-col gap-2.5">
      {PROVIDERS.map(({ provider, label, Icon }) => {
        const isBusy = mutation.isPending && mutation.variables?.provider === provider;
        return (
          <Button
            key={provider}
            type="button"
            variant="sso"
            size="sso"
            className="h-10 w-full justify-start gap-2.5 px-4 text-[13px] font-medium tracking-[-0.01em] sm:h-11 sm:px-[18px] sm:text-sm"
            disabled={mutation.isPending}
            onClick={() => {
              mutation.mutate(
                { provider, redirectTo },
                {
                  onError: (err) => {
                    const key = mapAuthError(err);
                    toast.error(t(`errors.${key ?? 'fallbackOauth'}`));
                  },
                },
              );
            }}
          >
            <Icon className="h-[18px] w-[18px] shrink-0" />
            <span className="flex-1 pr-7 text-center">
              {isBusy ? t('sso.redirecting') : `${prefix} ${label}`}
            </span>
          </Button>
        );
      })}
    </div>
  );
}
